import './ConsoleNavMenu.css';
import { useState, useEffect, ReactNode } from 'react'
import { logger } from '../../utils/logger';
import BatteryBox from './navMenu/BatteryBox';
import MsgBox from './navMenu/MsgBox';
import ModeBox from './navMenu/ModeBox';
import UserSettings from './navMenu/UserSettings';
import IconBackUrl from '../../assets/icons/back.svg'
import useDroneHubConnection from '../../hooks/DroneHubConnection';
import { ModemData } from '../../api/Drone';
import { HubHbStatusMessage, HubStatusTextMessage, HubTelemetryMessage } from '../../asp/DroneHub';
import { APIRequestGet } from '../../utils/APIRequests';
import { UserSchema } from '../../api/User';
import { BatteryRemainingData } from '../../types/Battery';

interface ConsoleNavMenuProps {
    drone?: ModemData | null;
    unselectModem?: () => void;
    toggleShowMenu?: () => void;
    startMsgs?: HubStatusTextMessage[];
}

function ConsoleNavMenu({ drone = null, toggleShowMenu, unselectModem, startMsgs }: ConsoleNavMenuProps): ReactNode {
    const droneHub = useDroneHubConnection();

    const [updateTelemFromHub, setUpdateTelemFromHub] = useState<HubTelemetryMessage | null>(null);
    const [hbUpdate, setHbUpdate] = useState<HubHbStatusMessage | null>(null);
    const [telemetry, setTelemetry] = useState<HubTelemetryMessage[]>([]);
    const [currentTelemetry, setCurrentTelemetry] = useState<HubTelemetryMessage | null>(null);

    const [login, setLogin] = useState<string>("");
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [missionPlannerUrl, setMissionPlannerUrl] = useState<string>("");
    const [imei, setImei] = useState<string>(drone?.imei ?? "");
    const [arm, setArm] = useState<boolean>(drone?.isArm ?? false);
    const [uavMode, setUavMode] = useState<number>(drone?.mode ?? -1);
    const [uavType, setUavType] = useState<number>(drone?.mavType ?? -1);
    const [mName, setMName] = useState<string>(drone?.name ?? "");
    const [isOnline, setIsOnline] = useState<boolean>(drone?.isOnline ?? false);

    const [_batteryRemaining, setBatteryRemaining] = useState<BatteryRemainingData[]>(drone?.batteryRemaining ?? []);

    const isAstra = process.env.REACT_APP_SERVICE === 'astra';

    useEffect(() => {
        APIRequestGet('/user/start', {
            schema: UserSchema,
            logSuccess: true
        }).then((result) => {
            setLogin(result.data.login);
            setIsAdmin(result.data.isAdmin);
            if (result.data.missionPlannerUrl) {
                setMissionPlannerUrl(result.data.missionPlannerUrl);
            }
        });
    }, [window.location.href]);

    useEffect(() => {
        logger.debug(process.env.REACT_APP_SERVICE);

        droneHub.connection.on('HbStatus', (message) => {
            setHbUpdate(message);
        });

        droneHub.connection.on('Telemetry', (message) => {
            setUpdateTelemFromHub(message);
        });
    }, [droneHub]);

    useEffect(() => {
        if (hbUpdate != null) {
            if (hbUpdate.imei === drone?.imei) {
                setArm(hbUpdate.isArm);
                setUavMode(hbUpdate.mode);
                setUavType(hbUpdate.type);
            }
        }
    }, [hbUpdate]);

    useEffect(() => {
        //logger.debug("update drone dd");
        if (drone != null) {
            setArm(drone.isArm);
            setUavMode(drone.mode);
            setImei(drone.imei);
            setBatteryRemaining(drone.batteryRemaining ?? []);
            setUavType(drone?.mavType);
        }
        //logger.debug(drone);
        setIsOnline(drone?.isOnline ?? false);
        setMName(drone?.name ?? "");
        switchTelemetry();
        //todo: request api
    }, [drone]);

    useEffect(() => {
        if (updateTelemFromHub) {
            setTelem(updateTelemFromHub);
        }
    }, [updateTelemFromHub]);

    useEffect(() => {
        logger.debug("toggleShowMenu rerender!");
    }, [toggleShowMenu]);

    function switchTelemetry() {
        if (drone === null) {
            return;
        }
        setCurrentTelemetry(telemetry.find((item) => item.imei === drone.imei) ?? null);
    }

    function setTelem(msg: HubTelemetryMessage): void {
        setTelemetry((telems) => {
            const itemExists = telems.some((item) => item.imei === msg.imei);
            if (itemExists) {
                return telems.map((t) =>
                    t.imei === msg.imei
                        //? { ...t, batteryRemaining: msg.batteryRemaining, height: msg.height, speed: msg.speed, sputniksCount: msg.sputniksCount }
                        ? { ...t, ...msg }
                        : t
                )
            } else {
                //return [...telems, {imei: msg.imei, batteryRemaining: msg.batteryRemaining, height: msg.height, speed: msg.speed, sputniksCount: msg.sputniksCount}];
                return [...telems, { ...msg }];
            }
        });

        if (msg?.imei === drone?.imei) {
            setCurrentTelemetry(msg);
        }
    }

    return (
        <header className="header">
            <div className='console-nav-menu t-big'>
                {!mName ? (
                    <>
                        {isAstra ? (
                            <div onClick={() => { window.location.href = '/' }} className='header-box logo-box'>
                                <img className='astra-icon logo' src="/image/astra/astra_logo.svg" alt="" />
                            </div>
                        ) : (
                            <div className='header-box logo-box'>Air-link Console</div>
                        )}
                        <div className='header-box menu-box'>
                            <UserSettings
                                login={login}
                                isAdmin={isAdmin}
                                missionPlannerUrl={missionPlannerUrl}
                            />
                        </div>
                    </>
                ) : (
                    <div className='telemetry-layout'>
                        <div
                            className='header-box icon-container mobile-back-btn'
                            onClick={() => { unselectModem?.(); }}
                        >
                            <img src={IconBackUrl} alt="" />
                        </div>

                        <div className="header-box header-left-box">
                            <div className='name-box'>{mName}</div>
                            <div className='arm-box'>{arm ? 'ARM' : 'DISARM'}</div>
                        </div>

                        <MsgBox imei={imei} startMsgs={startMsgs ?? []} />

                        <div className="header-box header-second-row-group">
                            <ModeBox type={uavType} mode={uavMode} isOnline={isOnline} />
                            <div className='sat-box'>
                                <img className='sat-box-icon' src="/image/modem/sat.svg" alt="" />
                                <div>{currentTelemetry?.sputniksCount ?? "nan"}</div>
                            </div>
                            <BatteryBox batteries={currentTelemetry?.batteryRemaining ?? null} />
                        </div>

                        <div className='header-box menu-box' >
                            <img
                                className='menu-box-sep'
                                onClick={toggleShowMenu}
                                src="/image/modem/settings.svg" alt=""
                            />
                            <UserSettings
                                login={login}
                                isAdmin={isAdmin}
                                missionPlannerUrl={missionPlannerUrl}
                            />
                        </div>
                    </div>
                )}
            </div>
        </header>
    )
}

export default ConsoleNavMenu;